/** Parse GET /friendlies/data and /training/data payloads. */
import moment from 'moment-timezone';
import { isMatchLive } from './liveMatch';
import { matchKickoff } from './localTime';

export const formatGoalProgress = (goal) => {
  if (!goal || typeof goal !== 'object') return '—';
  const g = Number(goal.goal);
  const tracker = Number(goal.tracker);
  if (Number.isFinite(g) && g > 0 && Number.isFinite(tracker)) {
    return `${Math.min(tracker, g)}/${g}`;
  }
  if (Number.isFinite(goal.remaining)) {
    return goal.remaining === 0 ? 'Done' : `${goal.remaining} left`;
  }
  if (goal.status === 1) return 'Done';
  return '—';
};

const looksLikeId = (value) =>
  typeof value === 'string' &&
  (/^[a-f0-9]{24}$/i.test(value) || /^[0-9a-f-]{8,}$/i.test(value));

const teamNameFrom = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || looksLikeId(trimmed)) return null;
    return trimmed;
  }
  if (typeof value !== 'object') return null;
  return (
    value.name ||
    value.team_name ||
    value.teamName ||
    value.clubName ||
    value.club_name ||
    null
  );
};

const offerIdFrom = (item) => {
  if (!item || typeof item !== 'object') return null;
  return (
    item._id ||
    item.offer ||
    item.offerId ||
    item.offer_id ||
    item.id ||
    item.requestId ||
    item.request_id ||
    item.friendlyId ||
    item.friendly_id ||
    item.trainingId ||
    item.training_id ||
    null
  );
};

/** Other club id candidates (offer id itself is separate). */
export const opponentIdsFrom = (item) => {
  if (!item || typeof item !== 'object') return [];
  const values = [
    item.opponent,
    item.team,
    item.club,
    item.from,
    item.to,
    item.from_team,
    item.to_team,
    item.fromTeam,
    item.toTeam,
    item.host_team,
    item.guest_team,
    item.sending_team,
    item.receiving_team,
    item.requesting_team,
    item.requested_team,
    item.teamId,
    item.team_id,
    item.opponentId,
    item.opponent_id,
    item.sender,
    item.receiver,
    item.user_team,
    item.other_team,
  ];
  const ids = [];
  values.forEach((value) => {
    if (!value) return;
    if (typeof value === 'string' && looksLikeId(value)) ids.push(value);
    else if (typeof value === 'object' && value._id) ids.push(String(value._id));
  });
  return [...new Set(ids.map(String))];
};

/** Prefer the club that is not mine (host/guest API shape). */
export const offerOpponentFrom = (item, myTeamId) => {
  if (!item || typeof item !== 'object') return null;
  const host = item.host_team;
  const guest = item.guest_team;
  if (host || guest) {
    const hostId = host?._id != null ? String(host._id) : null;
    const guestId = guest?._id != null ? String(guest._id) : null;
    const me = myTeamId != null ? String(myTeamId) : null;
    if (me && hostId === me) return guest || null;
    if (me && guestId === me) return host || null;
    return host || guest || null;
  }
  return (
    item.team ||
    item.opponent ||
    item.club ||
    item.from ||
    item.to ||
    item.sending_team ||
    item.receiving_team ||
    null
  );
};

export const offerLabelFrom = (item, myTeamId) => {
  if (!item || typeof item !== 'object') return null;
  const opponent = offerOpponentFrom(item, myTeamId);
  if (opponent) return teamNameFrom(opponent);
  return (
    item.teamName ||
    item.team_name ||
    item.opponentName ||
    item.opponent_name ||
    item.clubName ||
    item.name ||
    null
  );
};

const canAcceptFrom = (item) => {
  if (!item || typeof item !== 'object') return true;
  const value = item.canAccept ?? item.can_accept;
  if (typeof value === 'boolean') return value;

  // The API uses a numeric status here, not a boolean: 0 means that the
  // offer can be accepted, while a non-zero value describes a blocker.
  // Treating 0 as false inverted every numeric status in the inbox.
  if (typeof value === 'number') return value === 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '0' || normalized === 'true') return true;
    if (normalized === 'false' || /^\d+$/.test(normalized)) return false;
  }
  return true;
};

export const asOfferArray = (value, myTeamId) => {
  if (!Array.isArray(value)) return null;
  return value
    .map((item) => {
      if (typeof item === 'string' && item) {
        return {
          id: item,
          label: 'Unknown club',
          crest: null,
          opponentIds: [item],
          canAccept: true,
          raw: item,
        };
      }
      if (!item || typeof item !== 'object') return null;
      const id = offerIdFrom(item);
      const opponent = offerOpponentFrom(item, myTeamId);
      const opponentIds = opponentIdsFrom(item);
      const offerId = id || opponentIds[0];
      if (!offerId) return null;
      return {
        id: String(offerId),
        label: offerLabelFrom(item, myTeamId) || 'Unknown club',
        crest: opponent?.symbol || null,
        opponentIds,
        canAccept: canAcceptFrom(item),
        raw: item,
      };
    })
    .filter(Boolean);
};

/** Walk invite/home payloads and collect `_id` → `name`. */
export const collectTeamNameMap = (...sources) => {
  const map = {};
  const walk = (node, depth = 0) => {
    if (!node || depth > 6) return;
    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, depth + 1));
      return;
    }
    if (typeof node !== 'object') return;
    if (node._id && (node.name || node.team_name || node.teamName)) {
      map[String(node._id)] =
        node.name || node.team_name || node.teamName;
    }
    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (value && typeof value === 'object') walk(value, depth + 1);
    });
  };
  sources.forEach((source) => walk(source));
  return map;
};

export const resolveOfferLabels = (offers, nameById) => {
  if (!offers?.available) return offers;
  const resolve = (list) =>
    (list || []).map((offer) => {
      if (offer.label && offer.label !== 'Unknown club') return offer;
      const named = (offer.opponentIds || [])
        .map((id) => nameById[String(id)])
        .find(Boolean);
      if (named) return { ...offer, label: named };
      if (nameById[String(offer.id)]) {
        return { ...offer, label: nameById[String(offer.id)] };
      }
      return offer;
    });
  return {
    ...offers,
    sent: resolve(offers.sent),
    received: resolve(offers.received),
  };
};

const pickFromBag = (bag, myTeamId) => {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) {
    return { sent: null, received: null };
  }

  let sent =
    asOfferArray(bag.sent, myTeamId) ||
    asOfferArray(bag.outgoing, myTeamId) ||
    asOfferArray(bag.sentOffers, myTeamId) ||
    asOfferArray(bag.offers_sent, myTeamId) ||
    asOfferArray(bag.sent_offers, myTeamId);

  let received =
    asOfferArray(bag.received, myTeamId) ||
    asOfferArray(bag.incoming, myTeamId) ||
    asOfferArray(bag.receivedOffers, myTeamId) ||
    asOfferArray(bag.offers_received, myTeamId) ||
    asOfferArray(bag.received_offers, myTeamId);

  return { sent, received };
};

/** Inbox lists from /friendlies/data and /training/data. */
export const parseDevelopmentOffers = (payload, myTeamId) => {
  if (!payload || typeof payload !== 'object') {
    return { sent: null, received: null, available: false };
  }

  const picked = pickFromBag(payload, myTeamId);
  const available = picked.sent !== null || picked.received !== null;
  const offers = {
    sent: picked.sent || [],
    received: picked.received || [],
    available,
  };
  return resolveOfferLabels(offers, collectTeamNameMap(payload));
};

export const formatPastMatchRow = (match) => {
  if (!match || typeof match !== 'object') return null;
  const home = match.home_team || match.host_team;
  const away = match.away_team || match.guest_team;
  const homeName = home?.name || 'Home';
  const awayName = away?.name || 'Away';
  const hs = match.home_team_statistics?.current_score;
  const as_ = match.away_team_statistics?.current_score;
  const hasScore = Number.isFinite(hs) && Number.isFinite(as_);
  const score = hasScore ? `${hs}–${as_}` : match.status === 1 ? 'Done' : '—';
  const matchId = match._id || match.id || match.match_id || null;
  return {
    id: String(
      matchId ||
        `${homeName}-${awayName}-${match.match_date || match.createdAt || ''}`
    ),
    matchId: matchId ? String(matchId) : null,
    label: `${homeName} vs ${awayName}`,
    value: score,
    homeCrest: home?.symbol || null,
    awayCrest: away?.symbol || null,
    homeName,
    awayName,
  };
};

export const parsePastMatches = (payload) => {
  const list =
    (Array.isArray(payload?.pastMatches) && payload.pastMatches) ||
    (Array.isArray(payload?.pastTrainings) && payload.pastTrainings) ||
    (Array.isArray(payload?.pastPractices) && payload.pastPractices) ||
    null;
  if (!list) return { available: false, items: [] };
  return {
    available: true,
    items: list.map(formatPastMatchRow).filter(Boolean),
  };
};

export const parseDevelopmentPayload = (payload, myTeamId) => ({
  dailyTasks: payload?.dailyTasks || null,
  seasonTasks: payload?.seasonTasks || null,
  nextMatch: payload?.nextMatch || null,
  offers: parseDevelopmentOffers(payload, myTeamId),
  pastMatches: parsePastMatches(payload),
});

export const FRIENDLY_ACCEPT_ERRORS = {
  201: "Can't play more than one friendly at a time.",
  202: "Can't organize friendlies right now because of league/cup matches.",
};

export const TRAINING_ACCEPT_ERRORS = {
  201: 'This team already practiced today — try another team.',
  202: 'You can only practice with another team once a day.',
  203: "Can't organize training right now because of league/cup matches.",
};

export const acceptErrorMessage = (kind, status) => {
  const table = kind === 'training' ? TRAINING_ACCEPT_ERRORS : FRIENDLY_ACCEPT_ERRORS;
  return table[status] || 'We could not accept that offer.';
};

const CANNOT_ACCEPT_FALLBACK =
  "Can't accept this offer right now (already playing or too close to a scheduled match).";

/** Proactive UI block before Accept (live match, kickoff window, or API blocker). */
export const inviteAcceptBlockReason = ({
  kind,
  matchRunning,
  nextMatch,
  timezone,
  hoursBefore = 3,
  canAccept,
} = {}) => {
  if (isMatchLive(matchRunning)) {
    if (matchRunning?.friendly) {
      return kind === 'friendly'
        ? FRIENDLY_ACCEPT_ERRORS[201]
        : "Can't accept while a match is in progress.";
    }
    return "Can't accept while a match is in progress.";
  }

  const fixture = nextMatch?.match_date ? nextMatch : null;
  if (fixture && !fixture.friendly && !fixture.training) {
    const kickoff = matchKickoff(fixture.match_date, {
      timezone,
      isCup: Boolean(fixture.domestic_cup),
    });
    if (kickoff?.isValid()) {
      const hours = kickoff.diff(moment(), 'hours', true);
      if (hours >= 0 && hours <= hoursBefore) {
        return kind === 'training'
          ? TRAINING_ACCEPT_ERRORS[203]
          : FRIENDLY_ACCEPT_ERRORS[202];
      }
    }
  }

  if (canAccept === false) {
    return CANNOT_ACCEPT_FALLBACK;
  }
  return null;
};
